/* eslint-disable @next/next/no-img-element */
import React from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { NavigationOptions, RoleType } from 'interfaces';
import { Dropdown } from 'components/common/dropdown';
import { Avatar, Icon } from 'components/common';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid';
import { CypressI } from 'interfaces/cypress';
import { useRouter } from 'next/router';
import { Notifications } from './Notifications';
import AppRoutes from '@/const/routes';
import NavbarSearch from '../../dashboard/navbar-search/NavbarSearch';
import Icons from '@/const/icons';
import { useUser } from '@/hooks';
import { useQuery } from '@tanstack/react-query';
import { casesLawyerService, GET_LAWYER_CASE_DETAILS } from '@/api';
import { getValidCaseId } from '@/utils';

interface HeaderNavbarProps {
	firstname?: string;
	lastname?: string;
	photoUrl?: string;
	navBarOptions?: NavigationOptions[];
	logoUrl?: string;
	className?: string;
	showSidebar?: boolean;
	isLoading: boolean;
	role: RoleType | undefined;
	setShowSidebar?: (value: boolean) => void;
	showMobileSearchModal?: () => void;
}

/**
 *	This component is meant to be rendered as a header navBar
 * 	Its a basic component mostly customizable from the navBarOptions prop
 *
 * @param navBarOptions options to be rendered in the header
 * @param logoUrl The logo that will be shown in the left side of the header
 * @param className additional classes
 * @param showSidebar to show sidebar when it's mobile version
 * @param showMobileSearchModal Shows the search modal for mobile devices
 * @returns JSX element representing the header
 */
const HeaderNavbarDashboard: React.FC<HeaderNavbarProps & CypressI> = ({
	firstname,
	lastname,
	photoUrl,
	dataCY,
	navBarOptions,
	logoUrl,
	isLoading,
	className,
	role,
	showSidebar,
	setShowSidebar,
	showMobileSearchModal,
}) => {
	const router = useRouter();
	const { user } = useUser();

	// Case Dynamic Name
	const caseId = getValidCaseId(router.query.id);
	const { data: caseLawyerDetails } = useQuery({
		queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
		queryFn: () => casesLawyerService.getCaseDetails(caseId as string),
		enabled: !!caseId && router.pathname.includes('/lawyerapp/cases/[id]'),
	});

	const caseName = React.useMemo(() => {
		if (caseLawyerDetails?.data.title) {
			return caseLawyerDetails.data.title;
		}
		return '';
	}, [caseLawyerDetails]);

	const pageTitle = React.useMemo(() => {
		const title = navBarOptions?.find(
			(option) =>
				router.asPath.includes(option.href as string) ||
				router.pathname === (option.href as string)
		)?.label;

		// If the route is a case route and the title is 'Cases' or empty, return the case name
		if (caseId && caseName && (title === 'Cases' || !title)) {
			return caseName;
		}

		return title || '';
	}, [navBarOptions, router, caseName, caseId]);

	React.useEffect(() => {
		if (window?.innerWidth < 1024) {
			document
				.querySelector('.critical-heading')
				?.setAttribute('hidden', 'true');
		}
	}, []);

	const getAvatarImage = (): string => {
		if (isLoading) return Icons.avatar;
		if (!firstname) return Icons.avatar;
		if (!isLoading && photoUrl) return photoUrl;
		return '';
	};

	return (
		<nav
			data-cy={dataCY}
			className={clsx(
				'flex items-center w-full px-8 py-7 lg:py-[13px] relative bg-white z-20 ',
				{ 'justify-between': pageTitle },
				{ 'justify-end': !pageTitle },

				className
			)}
		>
			{/* Logo */}
			{logoUrl && (
				<Link
					href={
						role === 'client'
							? AppRoutes.CLIENTAPP_DASHBOARD
							: role === 'lawyer'
							? AppRoutes.LAWYERAPP_DASHBOARD
							: '#'
					}
					className="absolute lg:hidden left-4 md:left-8 top-4"
				>
					<Logo className="w-[163px] h-[30px]" />
				</Link>
			)}

			{/* Menu Icon (Show sidebar only mobile screen) */}
			<div
				className={clsx(
					'lg:hidden absolute right-3 md:right-8 top-4 flex gap-4 items-center',
					{
						'z-50': showSidebar,
					}
				)}
			>
				{user && user.role === 'lawyer' && (
					<Icon
						src={Icons.search}
						className="h-4 w-4"
						height={16}
						width={16}
						onClick={showMobileSearchModal}
					/>
				)}
				<Notifications classNameMenuItems="!-right-10 xs:!right-0" />
				<button
					type="button"
					onClick={() => setShowSidebar && setShowSidebar(!showSidebar)}
				>
					{showSidebar ? (
						<XMarkIcon width={20} height={20} className="text-black" />
					) : (
						<div className="flex lg:hidden items-center gap-3">
							<Bars3Icon width={20} height={20} className="text-black" />
						</div>
					)}
				</button>
			</div>

			{pageTitle && (
				<>
					{router.pathname === AppRoutes.CLIENTAPP_DASHBOARD ? (
						<h1 className="hidden lg:block critical-heading">
							Welcome
							{firstname ? (
								<>
									,<span className="text-primary"> {firstname}</span>
								</>
							) : null}
						</h1>
					) : (
						<h1 className="hidden lg:block critical-heading">{pageTitle}</h1>
					)}
				</>
			)}

			{/* Profile only in Desktop*/}
			<div className="hidden lg:flex items-center gap-6">
				<div className="xl:min-w-[410px]">
					{user && user.role === 'lawyer' && <NavbarSearch />}
				</div>
				<Notifications />
				<Dropdown
					buttonContent={
						<Avatar
							photoUrl={getAvatarImage()}
							size="small"
							firstname={firstname}
							lastname={lastname}
						/>
					}
					showChevronDownIcon
					classNameButton="w-auto text-white"
				/>
			</div>
		</nav>
	);
};

export default HeaderNavbarDashboard;

const Logo = (props: React.SVGProps<SVGSVGElement>) => (
	<svg
		width="260"
		height="48"
		viewBox="0 0 260 48"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		{...props}
	>
		<g clipPath="url(#clip0_19007_742)">
			<path
				d="M66.5075 16.4699C64.8982 16.4699 63.6419 17.1288 62.7421 18.4467C61.839 19.7645 61.3908 21.5817 61.3908 23.8981C61.3908 28.7144 63.2209 31.1226 66.8844 31.1226C67.9913 31.1226 69.0642 30.9697 70.1032 30.6607C71.1422 30.3516 72.1845 29.9813 73.2371 29.5466V35.2664C71.1489 36.1902 68.7858 36.6522 66.151 36.6522C62.3754 36.6522 59.4792 35.5551 57.4658 33.3643C55.4524 31.1735 54.444 28.0046 54.444 23.8608C54.444 21.2692 54.9329 18.9935 55.9074 17.0303C56.8818 15.0671 58.2875 13.559 60.1175 12.5027C61.9476 11.4464 64.1037 10.9199 66.5788 10.9199C69.2849 10.9199 71.8687 11.5075 74.3304 12.6827L72.2592 18.0085C71.3357 17.5738 70.4088 17.2103 69.4852 16.9114C68.5617 16.6125 67.5703 16.4665 66.5075 16.4665V16.4699Z"
				fill="#1D0254"
			/>
			<path
				d="M83.7083 36.312H77.0161V9.67285H83.7083V36.312Z"
				fill="#1D0254"
			/>
			<path
				d="M100.99 36.312L99.707 33.7442H99.5712C98.6715 34.8617 97.7513 35.6293 96.8142 36.0369C95.8805 36.4479 94.6684 36.6517 93.188 36.6517C91.3613 36.6517 89.9251 36.1048 88.876 35.0077C87.8268 33.9107 87.3005 32.372 87.3005 30.385C87.3005 28.3981 88.0203 26.7813 89.4566 25.7725C90.8962 24.7638 92.9775 24.1965 95.704 24.0709L98.9363 23.969V23.6938C98.9363 22.0975 98.1486 21.2959 96.5732 21.2959C95.1573 21.2959 93.3612 21.7748 91.1814 22.7326L89.2494 18.3171C91.5073 17.1521 94.3594 16.5713 97.8057 16.5713C100.291 16.5713 102.22 17.1861 103.581 18.419C104.943 19.652 105.628 21.374 105.628 23.5886V36.3086H100.99V36.312ZM96.0265 31.9305C96.8346 31.9305 97.5306 31.6723 98.1044 31.1595C98.6816 30.6466 98.9702 29.9775 98.9702 29.1555V27.6474L97.4322 27.7154C95.2286 27.7969 94.1285 28.6053 94.1285 30.1473C94.1285 31.3361 94.7635 31.9271 96.0265 31.9271V31.9305Z"
				fill="#1D0254"
			/>
			<path
				d="M110.046 12.0881C110.046 11.0828 110.324 10.3355 110.884 9.84643C111.441 9.35732 112.344 9.10938 113.587 9.10938C114.83 9.10938 115.736 9.36072 116.307 9.86341C116.877 10.3661 117.162 11.1065 117.162 12.0881C117.162 14.0514 115.97 15.033 113.587 15.033C111.203 15.033 110.046 14.0514 110.046 12.0881ZM116.908 36.3123H110.215V16.9146H116.908V36.3123Z"
				fill="#1D0254"
			/>
			<path
				d="M145.384 36.312V25.7657C145.384 24.4546 145.204 23.4696 144.844 22.8107C144.484 22.1552 143.917 21.8257 143.14 21.8257C142.114 21.8257 141.361 22.2639 140.882 23.1436C140.403 24.0233 140.162 25.3989 140.162 27.2704V36.3086H133.453V25.7623C133.453 24.4512 133.283 23.4662 132.94 22.8073C132.597 22.1518 132.044 21.8223 131.28 21.8223C130.231 21.8223 129.467 22.291 128.988 23.2251C128.509 24.1625 128.268 25.691 128.268 27.8138V36.3052H121.576V16.9075H126.625L127.464 19.3055H127.858C128.37 18.4156 129.114 17.7363 130.092 17.2675C131.066 16.7988 132.176 16.5645 133.419 16.5645C136.213 16.5645 138.176 17.4136 139.307 19.1153H139.836C140.383 18.3171 141.147 17.6921 142.121 17.2404C143.096 16.7886 144.172 16.5645 145.347 16.5645C147.639 16.5645 149.333 17.1521 150.43 18.3273C151.526 19.5025 152.073 21.289 152.073 23.687V36.3052H145.381L145.384 36.312Z"
				fill="#1D0254"
			/>
			<path
				d="M167.926 36.3124L162.809 18.2835H162.656C162.894 21.354 163.016 23.7383 163.016 25.44V36.3124H157.027V11.2832H166.028L171.246 29.0539H171.382L176.499 11.2832H185.517V36.3124H179.303V25.3381C179.303 24.7675 179.314 24.1357 179.327 23.4394C179.344 22.7432 179.422 21.0381 179.558 18.3208H179.405L174.356 36.3158H167.922L167.926 36.3124Z"
				fill="#8039DF"
			/>
			<path
				d="M203.162 36.312L201.879 33.7442H201.743C200.84 34.8617 199.923 35.6293 198.986 36.0369C198.052 36.4479 196.84 36.6517 195.356 36.6517C193.53 36.6517 192.094 36.1048 191.044 35.0077C189.995 33.9107 189.469 32.372 189.469 30.385C189.469 28.3981 190.189 26.7813 191.625 25.7725C193.065 24.7638 195.146 24.1965 197.872 24.0709L201.108 23.969V23.6938C201.108 22.0975 200.32 21.2959 198.745 21.2959C197.329 21.2959 195.533 21.7748 193.353 22.7326L191.421 18.3171C193.679 17.1521 196.531 16.5713 199.977 16.5713C202.466 16.5713 204.391 17.1861 205.753 18.419C207.114 19.652 207.797 21.374 207.797 23.5886V36.3086H203.159L203.162 36.312ZM198.198 31.9305C199.006 31.9305 199.702 31.6723 200.276 31.1595C200.853 30.6466 201.142 29.9775 201.142 29.1555V27.6474L199.601 27.7154C197.397 27.7969 196.297 28.6053 196.297 30.1473C196.297 31.3361 196.932 31.9271 198.198 31.9271V31.9305Z"
				fill="#8039DF"
			/>
			<path
				d="M229.948 16.9143V20.1512L227.296 21.1261C227.66 21.7884 227.843 22.5594 227.843 23.4391C227.843 25.528 227.11 27.1481 225.643 28.2996C224.176 29.451 221.989 30.0284 219.08 30.0284C218.36 30.0284 217.789 29.9843 217.368 29.8925C217.209 30.188 217.127 30.47 217.127 30.7315C217.127 31.0644 217.396 31.3157 217.932 31.4923C218.468 31.6689 219.168 31.7572 220.037 31.7572H223.256C227.602 31.7572 229.778 33.588 229.778 37.2529C229.778 39.6169 228.77 41.4544 226.756 42.7655C224.743 44.0765 221.915 44.7355 218.275 44.7355C215.525 44.7355 213.406 44.2769 211.916 43.3565C210.428 42.4394 209.681 41.1181 209.681 39.3927C209.681 37.0762 211.118 35.5818 213.993 34.9058C213.399 34.6545 212.883 34.2503 212.445 33.6899C212.007 33.1295 211.786 32.5758 211.786 32.029C211.786 31.4244 211.953 30.8911 212.282 30.4292C212.612 29.9673 213.304 29.417 214.353 28.7785C213.348 28.3335 212.561 27.6372 211.983 26.6896C211.406 25.742 211.118 24.5905 211.118 23.2319C211.118 21.1193 211.837 19.4821 213.274 18.3171C214.713 17.1521 216.764 16.5713 219.436 16.5713C219.789 16.5713 220.4 16.612 221.266 16.6902C222.132 16.7717 222.774 16.843 223.181 16.9143H229.941H229.948ZM215.335 38.727C215.335 39.2738 215.63 39.7188 216.224 40.055C216.818 40.3913 217.609 40.5611 218.604 40.5611C220.224 40.5611 221.521 40.3573 222.499 39.9531C223.473 39.5489 223.962 39.0259 223.962 38.3873C223.962 37.8744 223.667 37.511 223.073 37.3004C222.478 37.0898 221.63 36.9845 220.523 36.9845H217.905C217.185 36.9845 216.577 37.1544 216.082 37.4906C215.586 37.8269 215.338 38.2413 215.338 38.7304L215.335 38.727ZM217.44 23.2659C217.44 25.2529 218.129 26.2447 219.511 26.2447C220.149 26.2447 220.641 26.0001 220.991 25.5076C221.338 25.0185 221.514 24.2814 221.514 23.2998C221.514 21.2925 220.845 20.2871 219.511 20.2871C218.176 20.2871 217.44 21.2789 217.44 23.2659Z"
				fill="#8039DF"
			/>
			<path
				d="M232.756 12.0881C232.756 11.0828 233.034 10.3355 233.594 9.84643C234.155 9.35732 235.054 9.10938 236.301 9.10938C237.547 9.10938 238.45 9.36072 239.02 9.86341C239.591 10.3661 239.876 11.1065 239.876 12.0881C239.876 14.0514 238.684 15.033 236.301 15.033C233.917 15.033 232.759 14.0514 232.759 12.0881H232.756ZM239.618 36.3123H232.926V16.9146H239.618V36.3123Z"
				fill="#8039DF"
			/>
			<path
				d="M253.121 36.6556C249.95 36.6556 247.546 35.7996 245.906 34.0878C244.269 32.3759 243.451 29.9168 243.451 26.7105C243.451 23.5042 244.334 20.9975 246.103 19.2279C247.872 17.4583 250.388 16.5752 253.651 16.5752C255.898 16.5752 258.017 17.0677 260 18.0493L258.031 22.9981C257.219 22.6448 256.472 22.3629 255.786 22.1489C255.104 21.9383 254.387 21.833 253.647 21.833C252.564 21.833 251.726 22.2542 251.131 23.1C250.537 23.9457 250.242 25.1379 250.242 26.6765C250.242 29.781 251.389 31.3332 253.681 31.3332C255.644 31.3332 257.525 30.7626 259.328 29.6213V34.9471C257.606 36.0884 255.535 36.659 253.114 36.659L253.121 36.6556Z"
				fill="#8039DF"
			/>
			<path
				d="M34.8801 0H13.1025C5.86621 0 0 5.86833 0 13.1073V34.8927C0 42.1317 5.86621 48 13.1025 48H34.8801C42.1165 48 47.9827 42.1317 47.9827 34.8927V13.1073C47.9827 5.86833 42.1165 0 34.8801 0Z"
				fill="#8039DF"
			/>
			<path
				d="M22.7329 36.7563C13.511 34.5629 9.26276 31.6178 10.1045 28.0107C10.9461 24.4005 16.0483 23.6828 25.2671 25.8762C34.489 28.0695 38.7372 31.0146 37.8955 34.6248C37.0539 38.235 31.9517 38.9527 22.7329 36.7594V36.7563Z"
				fill="#14013B"
			/>
			<path
				d="M19.1087 12.0683L15.1171 29.2021L32.8821 33.4302L36.8737 16.2964L19.1087 12.0683Z"
				fill="#1D0254"
			/>
			<path
				d="M24.2916 30.0687C19.3853 28.9025 15.6298 27.0154 15.8992 25.8584L15.1218 29.2025C14.8524 30.3595 18.611 32.2435 23.5142 33.4129C28.4205 34.5792 32.6136 34.5885 32.883 33.4315L33.6604 30.0873C33.391 31.2443 29.1948 31.2381 24.2916 30.0687Z"
				fill="#C0D653"
			/>
			<g style={{ mixBlendMode: 'multiply' }}>
				<path
					d="M36.8741 16.2954L32.883 33.4276C32.681 34.2876 30.3181 34.5041 27.0952 34.0772L31.0802 16.9698L33.6635 15.5312L36.8711 16.2954H36.8741Z"
					fill="#A7A9AC"
				/>
			</g>
			<path
				d="M36.8686 16.3244C37.1382 15.1669 33.3793 13.2819 28.4728 12.1142C23.5663 10.9464 19.3702 10.9381 19.1006 12.0956C18.8309 13.2531 22.5898 15.138 27.4963 16.3058C32.4028 17.4735 36.5989 17.4818 36.8686 16.3244Z"
				fill="#290F5D"
			/>
		</g>
		<defs>
			<clipPath id="clip0_19007_742">
				<rect width="260" height="48" fill="white" />
			</clipPath>
		</defs>
	</svg>
);
